import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MultiSelectDropdown } from "@/components/ui/multi-select-dropdown";
import { useLanguage } from "@/contexts/LanguageContext";
import { TableHeaderProps } from "./types";
import { SearchBar } from "./search-bar";
import { RangeFilter } from "./range-filter";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export const TableHeader = ({
  searchTerm,
  onSearchChange,
  searchPlaceholder,
  filters = [],
  rangeFilters = [],
  toggle,
  onClearAll,
  tabGroups,
}: TableHeaderProps) => {
  const { t } = useLanguage();

  const hasActiveFilters =
    filters.some((filter) => filter.selectedValues.length > 0) ||
    rangeFilters.some((filter) => filter.value.min !== null || filter.value.max !== null);

  return (
    <div className="mb-4 mt-4 space-y-3">
      {/* First row: Tab groups */}
      {tabGroups && tabGroups.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          {tabGroups.map((group, index) => (
            <Tabs
              key={index}
              value={String(group.currentValue)}
              onValueChange={(value) => {
                const option = group.options.find((opt) => String(opt.value) === value);
                if (option) {
                  group.onValueChange(option.value);
                }
              }}
            >
              <TabsList>
                {group.options.map((tab) => (
                  <TabsTrigger key={String(tab.value)} value={String(tab.value)}>
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          ))}
        </div>
      )}

      {/* Second row: Filters and search */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* Left side: filters */}
        <div className="flex flex-wrap items-center gap-2 flex-grow">
          {/* Multi-select filters */}
          {filters.map((filter) => (
            <MultiSelectDropdown
              key={filter.label}
              label={filter.label}
              options={filter.options}
              selectedValues={filter.selectedValues}
              onChange={filter.onChange}
              showSearch={filter.showSearch}
              className="min-w-[140px] max-w-[180px] bg-transparent"
            />
          ))}

          {/* Range filters */}
          {rangeFilters.map((filter) => (
            <RangeFilter key={filter.label} filter={filter} />
          ))}
        </div>

        {/* Right side: search bar */}
        <div className="flex items-center gap-3">
          {/* Fallback to toggles if tab groups not provided */}
          {!tabGroups && toggle && (
            <div className="flex gap-2">
              {(Array.isArray(toggle) ? toggle : [toggle]).map((item, index) => (
                <button
                  key={index}
                  onClick={() => item.onCheckedChange(!item.checked)}
                  className={`
                    flex items-center gap-2 px-3 py-1.5 rounded-md text-sm
                    ${item.checked ? "bg-[#169C9D] text-white" : "text-gray-700 hover:bg-gray-50"}
                  `}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}

          <SearchBar searchTerm={searchTerm} onSearchChange={onSearchChange} placeholder={searchPlaceholder} />
        </div>
      </div>

      {/* Third row: Clear all button */}
      {hasActiveFilters && onClearAll && (
        <div className="flex justify-start">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAll}
            className="text-teal-600 hover:text-teal-600 hover:bg-teal-50/50 rounded-full"
          >
            <X className="h-4 w-4 mr-2" />
            {t("companies.filters.clearAll")}
          </Button>
        </div>
      )}
    </div>
  );
};
